import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import MaterialRatesTable from 'af-root/scenes/Company/Shared/Materials/MaterialRatesTable';

import type { W_MaterialCompanyMarkupHistory_FindForCompany_VM_Single } from 'ab-api/web/materialCompanyMarkupHistory/findForCompany';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import * as MaterialCompanyMarkupHistoryActions from 'af-actions/materialCompanyMarkupHistory';

import MaterialCompanyMarkupAdjustmentModal from './MaterialCompanyMarkupAdjustmentModal';
import MaterialCompanyMarkupFM from './MaterialCompanyMarkupAdjustmentModal/formModel';

type Props = ConnectedProps<typeof connector>;

const CompanyRatesTable: React.FC<Props> = (props) => {
	const { findMaterialMarkupHistoriesForCompany } = props;

	const [markupHistories, setMarkupHistories] = React.useState<W_MaterialCompanyMarkupHistory_FindForCompany_VM_Single[]>([]);
	const [initialFormValue, setInitialFormValue] = React.useState<Nullable<MaterialCompanyMarkupFM>>(null);

	const loadHistories = React.useCallback(async () => {
		const loaded = await findMaterialMarkupHistoriesForCompany();
		if (!loaded) {
			return;
		}
		setMarkupHistories(loaded.histories);
	}, [findMaterialMarkupHistoriesForCompany]);

	React.useEffect(() => {
		loadHistories();
	}, [loadHistories]);

	const handleSubmitSuccess = React.useCallback(() => {
		loadHistories();
	}, [loadHistories]);

	const closeModal = React.useCallback(() => {
		setInitialFormValue(null);
	}, []);

	const handleEdit = React.useCallback((initialValueSource: W_MaterialCompanyMarkupHistory_FindForCompany_VM_Single) => {
		setInitialFormValue(new MaterialCompanyMarkupFM(initialValueSource));
	}, []);

	return (
		<>
			<MaterialRatesTable
				entries={markupHistories}
				onEdit={handleEdit}
				rateSource={MaterialCostSource.COMPANY}
				title="Rates on Company Level"
				titleTooltip="Percentage-based rates configured at the company level, including shipping, sales markup, and tax rates, applied by default across all jobs unless overridden. Materials using a company-level rate are marked with a 'C' indicator in the Materials, Job Tracking, and Field Report Work Summary tables or 'M' if overridden on the company page."
			/>
			<MaterialCompanyMarkupAdjustmentModal
				close={closeModal}
				initialValues={initialFormValue}
				onSubmitSuccess={handleSubmitSuccess}
				showModal={!!initialFormValue}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findMaterialMarkupHistoriesForCompany: MaterialCompanyMarkupHistoryActions.findForCompany,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<unknown>>(
	React.memo,
	connector
);

export default enhance(CompanyRatesTable);
